"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { headerVariants, slideFromBottomVariants, cardVariants } from "@/lib/animations";

interface YachtCondition {
    key: string;
    label: string;
    value: string | boolean;
}

interface ProductYachtConditionsProps {
    conditions: YachtCondition[];
    title: string;
    readConditionsText: string;
    conditionsDescription: string;
}

export default function ProductYachtConditions({
    conditions,
    title,
    readConditionsText,
    conditionsDescription,
}: ProductYachtConditionsProps) {
    const [expandedConditions, setExpandedConditions] = useState(false);

    if (conditions.length === 0) return null;

    return (
        <motion.section 
            id="general-conditions"
            className="mb-[75px] scroll-mt-20"
            variants={slideFromBottomVariants}
        >
            <motion.h2 
                className="text-xl lg:text-3xl font-bold text-alsero-dark-gray mb-6"
                variants={headerVariants}
            >
                {title}
            </motion.h2>
            
            <motion.div 
                className="bg-gray-800 rounded-lg p-6 text-white mb-6"
                variants={cardVariants}
            >
                <button type="button"
                    onClick={() => setExpandedConditions(!expandedConditions)}
                    className="w-full flex items-center justify-between"
                >
                    <h3 className="text-xl font-semibold">{readConditionsText}</h3>
                    <span className="text-alsero-first font-bold text-2xl">
                        {expandedConditions ? '−' : '+'}
                    </span>
                </button>
                
                <AnimatePresence>
                    {expandedConditions && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden mt-4"
                        >
                            <p className="text-gray-300 leading-relaxed">
                                {conditionsDescription}
                            </p>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                variants={cardVariants}
            >
                {conditions.map((condition, index) => (
                    <div key={condition.key || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <span className="text-alsero-dark-gray font-medium">{condition.label}</span>
                        <span className={`font-semibold ${(condition.value === 'Yes' || condition.value === true) ? 'text-green-600' : 'text-red-600'}`}>
                            {String(condition.value)}
                        </span>
                    </div>
                ))}
            </motion.div>
        </motion.section>
    );
}

